import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Cell } from "react-table";
import { getContractApply } from "src/api/contract/contract-api";
import { ContractManageList, ContractStep } from "src/api/contract/contract-types";
import useApiOperation from "src/api/hooks/api-operation";
import { BaseInput, BaseRadio, BaseSelect, BaseTooltip } from "src/components";
import { BaseContractStatus } from "src/components/BaseContractStatus";
import ProductTypeChangeLabel from "src/components/ProductTypeChangeLabel";
import { getContractDetailPath } from "src/utils/route-util";
import BaseNewTabLink from "../BaseNewTabLink";
import TableSelectModal from "../TableSelectModal";

interface Contract {
  contractId: number;
  contractApplyNumber: string;
  contractManageId?: number | string;
  contractStep?: ContractStep;
  contractStepFilter?: any;
}

interface Props {
  onCanceled: () => void;
  onAdded: (partnerList: Contract[]) => void;
  defaultCheckedContractList: Contract[];
  contractStepFilter?: ContractStep[];
}

interface CellProps extends Cell<Contract> {
  checked: boolean;
}

const CONTRACT_TABLE_COLUMNS = [
  {
    Header: "선택",
    accessor: "checked",
    width: 80,
    Cell: (props: CellProps) => {
      // 재계약이 있으면 한 신청번호에 계약이 여러 건이므로, 행 식별은 계약 id로 한다.
      // 신청번호로 식별하면 같은 신청번호의 형제 계약이 전부 함께 선택된다.
      const contractId = props.row.original.contractId;
      return (
        // 선택 처리는 TableSelectModal이 이 div의 click을 받아서 하므로 라디오에 id를 주면 안 된다.
        // id를 주면 label(htmlFor)이 연결돼 label 클릭 → input 클릭이 한 번 더 올라와서 선택이 바로 해제된다.
        <div data-data-id={contractId} data-checked={props.value} className="checkbox">
          <BaseRadio id={""} name={""} checked={props.value} />
        </div>
      );
    },
  },
  {
    // 신청번호/계약번호 대체 컬럼. 계약 ID에 계약상세 링크가 있으므로 계약관리 ID는 링크를 걸지 않는다.
    Header: "계약관리 ID",
    accessor: "contractManageId",
    width: 120,
    Cell: (props: CellProps) => <div className="w-100">{props.value ? String(props.value) : "-"}</div>,
  },
  {
    Header: "계약 ID",
    accessor: "contractId",
    width: 80,
    Cell: (props: CellProps) => <BaseNewTabLink path={getContractDetailPath({ contractId: props.value })} value={props.value} className="w-100" />,
  },
  {
    Header: "계약상태",
    accessor: "contractStep",
    width: 80,
    Cell: (props: CellProps) => <BaseContractStatus status={props.value} />,
  },
  {
    Header: "계약명",
    accessor: "contractName",
    width: 225,
    Cell: (props: CellProps) => (
      <div className="w-100">
        <BaseTooltip contents={props.value ? props.value : "-"}></BaseTooltip>
      </div>
    ),
  },
  {
    Header: "상품명",
    accessor: "spaceProductName",
    width: 225,
    Cell: (props: CellProps) => (
      <div className="w-100">
        <BaseTooltip contents={props.value ? props.value : "-"}></BaseTooltip>
      </div>
    ),
  },
  {
    Header: "상품유형",
    accessor: "spaceProductType",
    width: 80,
    Cell: (props: CellProps) => <ProductTypeChangeLabel productType={props.value} />,
  },
  {
    Header: "계약자(법인명/상호)",
    accessor: "mbOrganizationName",
    width: 175,
    Cell: (props: CellProps) => (
      <div className="w-100">
        <BaseTooltip contents={props.value ? props.value : "-"}></BaseTooltip>
      </div>
    ),
  },
];

function ContractSelectModal({ onCanceled, onAdded, defaultCheckedContractList, contractStepFilter }: Props) {
  const [page, setPage] = useState({ current: 0, total: 0, totalElements: 0 });
  const [contractList, setContractList] = useState<ContractManageList[]>([]);
  const [selectedContractList, setSelectedContractList] = useState<Contract[]>(defaultCheckedContractList);
  const { handleSubmit, getValues, control } = useForm<{ searchWord: string; availableFilterList: string }>({
    defaultValues: {
      availableFilterList: contractStepFilter?.join(","),
    },
  });

  const searchFormRef = useRef<HTMLFormElement>(null);

  const contractListTableData = contractList.map((contract) => {
    const selectedIdList = selectedContractList.map((contract) => String(contract.contractId));

    return {
      checked: selectedIdList.includes(String(contract.contractId)),
      ...contract,
      contractStepFilter,
    };
  });

  const { executeAsync: getContractApplyAsync } = useApiOperation(getContractApply);

  const search = ({ searchWord, contractStep, page }: { page: number; searchWord?: string; contractStep?: string }) => {
    fetchContractList({ page, searchWord, contractStep });
  };

  const onSubmit = ({ searchWord }: { searchWord: string }) => search({ page: 0, searchWord, contractStep: getValues("availableFilterList") });
  const goPage = (nextPage: number) =>
    search({ page: nextPage, searchWord: getValues("searchWord"), contractStep: getValues("availableFilterList") });

  const fetchContractList = async ({ searchWord, contractStep, page }: { page: number; searchWord?: string; contractStep?: string }) => {
    const result = await getContractApplyAsync({
      page,
      searchValue: searchWord,
      size: 20,
      contractStep,
      sort: {
        orders: [
          {
            property: "contractManageId",
            direction: "DESC",
          },
        ],
      },
    });

    setContractList(result.data.data.content);

    setPage({
      current: result.data.meta.pageMeta?.pageRequest.page || 0,
      total: result.data.meta.pageMeta?.totalPages || 0,
      totalElements: result.data.meta.pageMeta?.totalElements || 0,
    });
  };
  const selectContract = (contractId: string) => {
    const selectedContract = contractList.find((contract) => String(contract.contractId) === contractId);
    setSelectedContractList([
      {
        contractId: selectedContract?.contractId || 0,
        contractApplyNumber: selectedContract?.contractApplyNumber || "",
        contractManageId: selectedContract?.contractManageId,
        contractStep: selectedContract?.contractStep as ContractStep,
      },
    ]);
  };
  const unSelectContract = (contractId: string) => {
    const filteredContractList = selectedContractList.filter((contract) => String(contract.contractId) !== contractId);
    setSelectedContractList(filteredContractList);
  };
  const _onAdded = () => onAdded(selectedContractList);

  const handleSearchClick = () => {
    searchFormRef.current?.dispatchEvent(
      new Event("submit", {
        bubbles: true,
      }),
    );
  };

  useEffect(() => {
    fetchContractList({
      page: 0,
      contractStep: contractStepFilter?.join(",") || "",
      searchWord: "",
    });
  }, []);

  return (
    <TableSelectModal<ContractManageList>
      onCanceled={onCanceled}
      onAdded={_onAdded}
      goPage={goPage}
      page={page}
      columns={CONTRACT_TABLE_COLUMNS}
      items={contractListTableData}
      title={"신청/계약"}
      select={selectContract}
      unSelect={unSelectContract}
      titleFormRender={() => {
        return (
          <form onSubmit={handleSubmit(onSubmit)} ref={searchFormRef} className="flex-center-center">
            <Controller
              name="availableFilterList"
              control={control}
              render={({ field }) => (
                <BaseSelect
                  className="mr8 minmax140"
                  value={field.value}
                  stateOptions={[
                    {
                      label: "전체",
                      value: "",
                    },
                    {
                      label: "사용가능",
                      value: contractStepFilter?.join(",") || "",
                    },
                  ]}
                  setStateValue={(value: string) => {
                    search({ page: 0, contractStep: value, searchWord: getValues("searchWord") });
                    field.onChange(value);
                  }}
                />
              )}
            />
            <Controller
              name="searchWord"
              control={control}
              render={({ field }) => (
                <BaseInput
                  placeholder="검색어를 입력하세요"
                  value={field.value}
                  onChange={field.onChange}
                  onSearchClick={handleSearchClick}
                  onKeyUp={handleSearchClick}
                  clearable
                />
              )}
            />
          </form>
        );
      }}
    />
  );
}
export default ContractSelectModal;
